import React from 'react';
import isEqual from 'react-fast-compare';

import DataConnector from 'connect/DataConnector';
import {StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import getDefaultLibrarySearchParams from 'notebook/library/library-sidepanel/getDefaultLibrarySearchParams';
import TemplateCollectionsSidepanel from 'notebook/template-collections/TemplateCollectionsSidepanel';
import {SearchParams, SortKey} from 'search/SearchConstants';

import {NavStoreProjectGraphQL} from '../../../../graphql-fragments/nav/__generated/NavStoreProjectGraphQL';
import {ProjectsAndTemplatesSectionTabs} from './ProjectsAndTemplatesSectionTabs';
import ProjectsListing from './ProjectsListing';
import ProjectsSidepanelListing from './ProjectsSidepanelListing';

interface Props {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  projects: Array<NavStoreProjectGraphQL>;
  areProjectsLoading: boolean;
}
interface EnhancedProps {
  listedFolder: StringIDFolder | null;
  onListAllSources: () => void;
  onListFolder: (rootFolder: StringIDFolder) => void;
  onResetTemplateCollections: () => void;
  currentSection: NavConstants.SidepanelSection | null;
}

class ProjectsSidepanelConnector extends DataConnector<Props, EnhancedProps> {
  stores = [SidepanelStore];

  onListFolder = (rootFolder: StringIDFolder, searchParams?: SearchParams): void => {
    this.dispatch(SidepanelReduxActions.listFolder(rootFolder, searchParams));
  };

  onListAllSources = (): void => {
    SidepanelActions.listAllSources(NavConstants.Section.PROJECTS);
  };

  onResetTemplateCollections = (): void => {
    this.dispatch(
      SidepanelReduxActions.openListing(
        NavConstants.Section.TEMPLATE_COLLECTIONS,
        getDefaultLibrarySearchParams()
      )
    );
  };

  enhanceProps(): EnhancedProps {
    const currentSection = this.sidepanelStore().getSection();
    return {
      onListFolder: this.onListFolder,
      onListAllSources: this.onListAllSources,
      onResetTemplateCollections: this.onResetTemplateCollections,
      listedFolder: currentSection ? this.sidepanelStore().getListedFolder(currentSection) : null,
      currentSection,
    };
  }
}

/**
 * This component renders either the ProjectsSidepanel or the TemplateCollectionsSidepanel, depending on what
 * the currentSection is. It also handles wiring up the section tabs, if they are available, to toggle back
 * and forth between the ProjectsSidepanel and the TemplateCollectionsSidepanel.
 */
function ProjectsAndTemplatesSidepanel(props: Props & EnhancedProps): JSX.Element {
  const {onListAllSources, onResetTemplateCollections, currentSection} = props;

  // Create reset handler for projects and template collections
  const resetHandler = React.useCallback(
    (section: NavConstants.Section): void => {
      if (section === NavConstants.Section.PROJECTS) {
        // Reset to top level projects view
        onListAllSources();
      } else if (section === NavConstants.Section.TEMPLATE_COLLECTIONS) {
        // Reset to top level template collections view
        onResetTemplateCollections();
      }
    },
    [onListAllSources, onResetTemplateCollections]
  );

  const sectionTabs = (
    <ProjectsAndTemplatesSectionTabs currentSection={currentSection} onSectionReset={resetHandler} />
  );

  if (currentSection !== NavConstants.Section.TEMPLATE_COLLECTIONS) {
    return <ProjectsSidepanel {...props} sectionTabs={sectionTabs} />;
  }

  return <TemplateCollectionsSidepanel {...props} sectionTabs={sectionTabs} />;
}

function ProjectsSidepanel(props: Props & EnhancedProps & {sectionTabs?: JSX.Element}): JSX.Element {
  if (props.listedFolder == null) {
    return (
      <ProjectsListing
        isExpanded={props.isExpanded}
        onListFolder={props.onListFolder}
        availableSortKeys={[SortKey.SORT_NAME_KEY, SortKey.SORT_CREATED_AT_KEY]}
        // TODO(Ben): Remove these properties
        areProjectsLoading={false}
        didLoadingError={false}
        items={[]}
        sectionTabs={props.sectionTabs}
      />
    );
  }

  return (
    <ProjectsSidepanelListing
      isCompactDisplay={props.isCompactDisplay}
      setIsCompactDisplay={props.setIsCompactDisplay}
      isExpanded={props.isExpanded}
      activeItemId={props.activeItemId}
      onNavigateToRoot={props.onListAllSources}
      onOpenFolder={props.onListFolder}
      folder={props.listedFolder}
      sectionTabs={props.sectionTabs}
    />
  );
}

export default ProjectsSidepanelConnector.connect(ProjectsAndTemplatesSidepanel, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});
